"use client";

import { useState } from "react";
import { Icon } from "./Icon";
import { cn } from "@/lib/utils";

export interface SendEmailActionProps {
  label?: string;
  body: string; // contenu déjà rédigé (pas de génération IA ici)
  defaultSubject?: string;
  defaultTo?: string;
  disabled?: boolean;
  tag?: string;
  onSent?: (to: string) => void;
}

type State =
  | { kind: "idle" }
  | { kind: "editing"; subject: string; body: string; to: string }
  | { kind: "sending"; subject: string; body: string; to: string }
  | { kind: "sent" }
  | { kind: "error"; message: string };

/** Bouton "Envoyer par mail" — reprend un contenu déjà rédigé dans le chat
 *  (réponse d'agent, message client...) sans repasser par une génération IA,
 *  laisse l'utilisateur ajuster destinataire/objet/corps, puis envoie via
 *  l'intégration Gmail existante. */
export function SendEmailAction({ label = "Envoyer par mail", body, defaultSubject, defaultTo, disabled, tag, onSent }: SendEmailActionProps) {
  const [state, setState] = useState<State>({ kind: "idle" });

  const open = () => {
    const firstLine = body.split("\n").find((l) => l.trim().length > 5) ?? "";
    const subject = defaultSubject ?? firstLine.replace(/^[#>*-]+\s*/, "").replace(/\*\*/g, "").slice(0, 90) ?? "Message";
    setState({ kind: "editing", subject, body, to: defaultTo ?? "" });
  };

  const send = async () => {
    if (state.kind !== "editing") return;
    if (!state.to.trim()) return;
    setState({ ...state, kind: "sending" });
    try {
      const res = await fetch("/api/integrations/gmail/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: state.to.trim(), subject: state.subject, body: state.body, tag: tag ?? label }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setState({ kind: "sent" });
      onSent?.(state.to.trim());
      setTimeout(() => setState({ kind: "idle" }), 4000);
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : "Erreur d'envoi" });
    }
  };

  return (
    <>
      {state.kind === "sent" ? (
        <span className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
          <Icon name="CheckCircle" size={12} /> Envoyé
        </span>
      ) : (
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          className="flex items-center gap-1.5 rounded-lg border border-[#0a1410]/15 bg-white/90 backdrop-blur-xl px-3 py-1.5 text-xs font-bold text-[#0a1410] hover:bg-white shadow-[0_4px_14px_-4px_rgba(0,0,0,0.4)] hover-lift disabled:opacity-60"
        >
          <Icon name="Send" size={12} /> {label}
        </button>
      )}

      {state.kind === "error" && (
        <span className="text-[11px] text-red-700">{state.message}</span>
      )}

      {(state.kind === "editing" || state.kind === "sending") && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
          onClick={() => state.kind === "editing" && setState({ kind: "idle" })}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="flex w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl max-h-[90vh]"
          >
            <header className="flex items-center justify-between gap-3 border-b border-[var(--color-line)] px-5 py-3">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[#0a1410] text-white">
                  <Icon name="Send" size={14} />
                </div>
                <h2 className="text-sm font-semibold text-[#0a1410]">Envoyer par mail</h2>
              </div>
              <button
                type="button"
                onClick={() => state.kind === "editing" && setState({ kind: "idle" })}
                disabled={state.kind === "sending"}
                className="rounded p-1 hover:bg-[var(--color-marine-50)]"
              >
                <Icon name="X" size={16} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">À</label>
                <input
                  type="email"
                  value={state.to}
                  onChange={(e) => setState({ ...state, to: e.target.value })}
                  disabled={state.kind === "sending"}
                  placeholder="email@destinataire.com"
                  className="mt-1 w-full rounded-md border border-[var(--color-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-marine-700)] disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">Objet</label>
                <input
                  type="text"
                  value={state.subject}
                  onChange={(e) => setState({ ...state, subject: e.target.value })}
                  disabled={state.kind === "sending"}
                  className="mt-1 w-full rounded-md border border-[var(--color-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-marine-700)] disabled:opacity-60"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-muted)]">Corps</label>
                <textarea
                  value={state.body}
                  onChange={(e) => setState({ ...state, body: e.target.value })}
                  disabled={state.kind === "sending"}
                  rows={12}
                  className="mt-1 w-full resize-y rounded-md border border-[var(--color-line)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-marine-700)] disabled:opacity-60 font-sans"
                />
              </div>
            </div>

            <footer className="flex items-center justify-end gap-2 border-t border-[var(--color-line)] bg-[var(--color-surface)] px-5 py-3">
              <button
                type="button"
                onClick={() => setState({ kind: "idle" })}
                disabled={state.kind === "sending"}
                className="rounded-md border border-[var(--color-line)] bg-white px-3 py-1.5 text-xs text-[#0a1410] hover:bg-[var(--color-marine-50)] disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={send}
                disabled={state.kind === "sending" || !state.to.trim()}
                className={cn(
                  "flex items-center gap-1.5 rounded-md bg-[#0a1410] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0a1410]/90 disabled:opacity-50"
                )}
              >
                {state.kind === "sending" ? (
                  <>
                    <Icon name="Loader" size={12} className="animate-spin" /> Envoi…
                  </>
                ) : (
                  <>
                    <Icon name="Send" size={12} /> Envoyer
                  </>
                )}
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
